import { useEffect, useState, useCallback } from "react";
import { Cookie, Settings as SettingsIcon, Check, ShieldCheck, BarChart3, Megaphone, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import {
    applyConsentMode,
    getConsent,
    injectInitialConsent,
    injectTrackingScripts,
    setConsent,
} from "@/lib/tracking";
import api from "@/lib/api";

const COOKIE_OPEN_EVENT = "bm-open-cookie-prefs";

/** Public helper — call this from anywhere (Footer link) to re-open the banner. */
export function openCookiePreferences() {
    window.dispatchEvent(new CustomEvent(COOKIE_OPEN_EVENT));
}

/**
 * Synchronously decide on the very first render whether the banner should be open.
 * This avoids the 1-frame flash where the banner mounts hidden, then opens via useEffect.
 * SSR-safe (returns false if no window).
 */
function hasStoredConsent() {
    if (typeof window === "undefined") return true;
    try {
        return !!window.localStorage.getItem("bm_consent");
    } catch {
        return false; // private mode etc. — show banner
    }
}

export default function CookieBanner() {
    const { t } = useI18n();
    // Skip the cookie banner entirely on internal cockpit routes. These pages
    // are NOT public funnel traffic, do not run marketing pixels, and
    // showing a GDPR consent dialog there would be misleading + intercept
    // pointer events for internal staff. Keeps the public funnel behaviour
    // untouched (banner still shows on /, /thank-you, /admin/*, /login, etc.).
    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    const isInternalCockpit = path.startsWith("/radar") || path.startsWith("/preview/");
    // Sync initial state — banner visible from first paint if no consent stored
    const [open, setOpen] = useState(() => !isInternalCockpit && !hasStoredConsent());
    const [showSettings, setShowSettings] = useState(false);
    const [consent, setLocal] = useState({ necessary: true, statistics: false, marketing: false });
    const [settings, setSettings] = useState(null);

    // Inject Consent Mode default-denied IMMEDIATELY on module mount (before paint).
    // This guarantees that GTM/GA4/Pixel won't fire prior to opt-in.
    // Skip on internal cockpit routes — no public tracking there.
    useEffect(() => {
        if (isInternalCockpit) return;
        injectInitialConsent();
        const c = getConsent();
        if (c) {
            setLocal(c);
            applyConsentMode(c);
        }
        api.get("/settings/public")
            .then(({ data }) => {
                setSettings(data);
                if (c) injectTrackingScripts(data, c);
            })
            .catch(() => {});
    }, []);

    // External trigger from Footer link or anywhere else
    const handleOpen = useCallback(() => {
        const c = getConsent();
        if (c) setLocal(c);
        setShowSettings(true);
        setOpen(true);
    }, []);

    useEffect(() => {
        window.addEventListener(COOKIE_OPEN_EVENT, handleOpen);
        return () => window.removeEventListener(COOKIE_OPEN_EVENT, handleOpen);
    }, [handleOpen]);

    // Lock body scroll on mobile when banner is open (so user must interact)
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        // Only lock scroll on mobile (where banner takes full bottom sheet)
        if (window.innerWidth < 768) {
            document.body.style.overflow = "hidden";
        }
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    const apply = (kind) => {
        let c;
        if (kind === "all") c = { necessary: true, statistics: true, marketing: true };
        else if (kind === "necessary") c = { necessary: true, statistics: false, marketing: false };
        else c = { ...consent, necessary: true };
        setConsent(c);
        applyConsentMode(c);
        if (settings) injectTrackingScripts(settings, c);
        setOpen(false);
        setShowSettings(false);
    };

    if (!open) return null;

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-title"
            data-testid="cookie-banner"
            // Highest z-index — over everything (modals are at 60-70).
            className="fixed inset-0 z-[100] flex items-end md:items-end md:justify-end pointer-events-none"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
            {/* Dimmed backdrop — pointer-events caught here so user is forced to choose */}
            <div
                className="absolute inset-0 bg-black/55 backdrop-blur-[2px] pointer-events-auto animate-[bm-fade-up_.35s_ease-out_both]"
                aria-hidden="true"
            />

            <div className="relative w-full md:w-auto md:max-w-2xl md:m-6 pointer-events-auto animate-[bm-fade-up_.4s_cubic-bezier(.2,.8,.2,1)_both]">
                <div
                    className="relative rounded-t-3xl md:rounded-3xl border border-white/10 overflow-hidden shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(11,17,32,0.98) 100%)",
                        backdropFilter: "blur(18px)",
                        WebkitBackdropFilter: "blur(18px)",
                    }}
                >
                    {/* Glow accent */}
                    <div
                        className="absolute inset-0 opacity-[0.06] pointer-events-none"
                        style={{
                            backgroundImage:
                                "radial-gradient(120% 80% at 0% 0%, rgba(16,185,129,1) 0%, transparent 55%)",
                        }}
                        aria-hidden="true"
                    />

                    <div className="relative p-5 sm:p-6 md:p-7 max-h-[85vh] overflow-y-auto">
                        {/* Header */}
                        <div className="flex items-center gap-3">
                            <div className="size-10 rounded-2xl bg-white/[0.05] border border-white/10 flex items-center justify-center">
                                <Cookie className="size-5 text-[#C8A96B]" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.24em] text-[#10B981] font-semibold">
                                    DSGVO · Consent Mode v2
                                </div>
                                <h2 id="cookie-title" className="font-display text-lg sm:text-xl font-bold text-white mt-0.5">
                                    {t("cookie.title")}
                                </h2>
                            </div>
                        </div>

                        {/* Intro */}
                        <p className="mt-4 text-sm text-[#CBD5E1] leading-relaxed">
                            {t("cookie.text")}{" "}
                            <Link
                                to="/datenschutz"
                                onClick={() => setOpen(false)}
                                className="text-[#10B981] hover:text-[#34D399] underline underline-offset-4 decoration-[#10B981]/30 hover:decoration-[#34D399]"
                            >
                                Datenschutzerklärung
                            </Link>
                        </p>

                        {/* Categories */}
                        {showSettings ? (
                            <div className="mt-5 space-y-2.5">
                                <CategoryRow
                                    icon={Lock}
                                    label={t("cookie.cat_necessary")}
                                    sublabel="Login, Sicherheit, Sprache. Diese Cookies sind technisch notwendig."
                                    checked={true}
                                    locked
                                    lockedLabel={t("cookie.necessary_locked")}
                                />
                                <CategoryRow
                                    icon={BarChart3}
                                    label={t("cookie.cat_stats")}
                                    sublabel="Google Analytics 4 · Reichweite & Verhaltensanalyse (anonymisiert)."
                                    checked={consent.statistics}
                                    onChange={(v) => setLocal((c) => ({ ...c, statistics: v }))}
                                    testid="cookie-toggle-statistics"
                                />
                                <CategoryRow
                                    icon={Megaphone}
                                    label={t("cookie.cat_marketing")}
                                    sublabel="Meta Pixel · Google Ads · Conversion-Tracking & Remarketing."
                                    checked={consent.marketing}
                                    onChange={(v) => setLocal((c) => ({ ...c, marketing: v }))}
                                    testid="cookie-toggle-marketing"
                                />
                            </div>
                        ) : null}

                        {/* Actions */}
                        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                            {showSettings ? (
                                <>
                                    <button
                                        onClick={() => setShowSettings(false)}
                                        className="order-3 sm:order-1 px-4 py-3 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-white border border-white/10 font-semibold text-sm transition"
                                    >
                                        Abbrechen
                                    </button>
                                    <button
                                        onClick={() => apply("necessary")}
                                        data-testid="cookie-necessary-btn"
                                        className="order-2 px-4 py-3 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-white border border-white/10 font-semibold text-sm transition"
                                    >
                                        {t("cookie.necessary")}
                                    </button>
                                    <button
                                        onClick={() => apply("custom")}
                                        data-testid="cookie-save-btn"
                                        className="order-1 sm:order-3 px-4 py-3 rounded-xl bg-[#10B981] hover:bg-[#0EA674] text-white font-semibold text-sm shadow-[0_14px_30px_-12px_rgba(16,185,129,0.55)] transition inline-flex items-center justify-center gap-2"
                                    >
                                        <Check className="size-4" /> {t("cookie.save")}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={() => apply("necessary")}
                                        data-testid="cookie-necessary-btn"
                                        className="px-4 py-3 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-white border border-white/10 font-semibold text-sm transition"
                                    >
                                        {t("cookie.necessary")}
                                    </button>
                                    <button
                                        onClick={() => setShowSettings(true)}
                                        data-testid="cookie-settings-btn"
                                        className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-white border border-white/10 font-semibold text-sm transition"
                                    >
                                        <SettingsIcon className="size-4" /> {t("cookie.settings")}
                                    </button>
                                    <button
                                        onClick={() => apply("all")}
                                        data-testid="cookie-accept-all-btn"
                                        className="px-4 py-3 rounded-xl bg-[#10B981] hover:bg-[#0EA674] text-white font-semibold text-sm shadow-[0_14px_30px_-12px_rgba(16,185,129,0.55)] transition"
                                    >
                                        {t("cookie.accept_all")}
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Footer micro */}
                        <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[#94A3B8]">
                            <span className="inline-flex items-center gap-1.5">
                                <ShieldCheck className="size-3.5 text-[#10B981]" />
                                Server in der EU
                            </span>
                            <Link to="/datenschutz" onClick={() => setOpen(false)} className="hover:text-white">
                                Datenschutz
                            </Link>
                            <Link to="/impressum" onClick={() => setOpen(false)} className="hover:text-white">
                                Impressum
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function CategoryRow({ icon: Icon, label, sublabel, checked, onChange, locked, lockedLabel, testid }) {
    return (
        <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-white/[0.025] border border-white/10">
            <div className="size-9 shrink-0 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center">
                <Icon className="size-4 text-[#94A3B8]" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <div className="text-sm font-semibold text-white">{label}</div>
                    {locked ? (
                        <span className="text-[9px] uppercase tracking-[0.22em] font-bold text-[#C8A96B] bg-[#C8A96B]/10 border border-[#C8A96B]/30 px-1.5 py-0.5 rounded">
                            {lockedLabel}
                        </span>
                    ) : null}
                </div>
                <div className="text-xs text-[#94A3B8] mt-0.5 leading-relaxed">{sublabel}</div>
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={!!checked}
                aria-disabled={locked || undefined}
                data-testid={testid}
                onClick={() => !locked && onChange?.(!checked)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border transition-colors duration-200 ${
                    checked ? "bg-[#10B981] border-[#10B981]" : "bg-white/[0.05] border-white/15"
                } ${locked ? "opacity-70 cursor-not-allowed" : ""}`}
            >
                <span
                    className={`inline-block size-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                        checked ? "translate-x-6" : "translate-x-1"
                    } mt-0.5`}
                />
            </button>
        </div>
    );
}
